import { forwardRef, HTMLAttributes, Ref, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { IComponent } from '../../models/components.models';
import { useClassNames } from '../../utils/hooks';
import { KeyValuePair, KeyValuePairSubmitOutcome } from './KeyValuePair';
import {
  IKeyValuePairEditorClassnames,
  IKeyValuePairEditorStyles,
  keyValuePairEditorStyles,
} from './KeyValuePair.styles';

export interface KeyValueValidation {
  keyValidation?: string;
  valueValidation?: string;
}

export type KeyValuePairValidationFn = (keyValue: { key?: string; value?: string }) => KeyValueValidation;

export interface IKeyValuePairEditorProps extends IComponent<IKeyValuePairEditorStyles>, HTMLAttributes<any> {
  emptyStateMessage?: string;
  config: Record<string, string>;
  onUpdateConfig: (newConfig: Record<string, string>) => void;
  validation?: KeyValuePairValidationFn;
  allowEmptyValues?: boolean;
  keyLabel?: string;
  valueLabel?: string;
  readOnly?: boolean;
  editOnly?: boolean;
  onAddNewLine?: () => void;
  directlyEditable?: boolean;
}

export const KeyValuePairEditor = forwardRef<HTMLDivElement, IKeyValuePairEditorProps>(
  (props: IKeyValuePairEditorProps, ref: Ref<HTMLDivElement>) => {
    const elementRef = useRef<HTMLDivElement | null>(null);
    const keyValueRef = useRef<any>(null);
    useImperativeHandle(
      ref,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      () => {
        return {
          submitKeyValue() {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            return keyValueRef.current ? keyValueRef.current.submitKeyValue() : KeyValuePairSubmitOutcome.UNSUCCESSFUL;
          },
          ...elementRef.current,
        };
      },
      []
    );
    const {
      emptyStateMessage = props.readOnly ? 'No data to display.' : 'Click the + button to add a new config item.',
      config,
      onUpdateConfig,
      validation,
      allowEmptyValues,
      className,
      styles,
      keyLabel,
      valueLabel,
      readOnly,
      editOnly,
      directlyEditable,
      ...elementProps
    } = props;
    const classNames = useClassNames<IKeyValuePairEditorStyles, IKeyValuePairEditorClassnames>(
      'KeyValuePairEditor',
      keyValuePairEditorStyles,
      styles,
      className
    );
    const items = useMemo<[string, string][]>(() => Object.entries(config), [config]);
    const [hasNew, setHasNew] = useState<boolean>(false);
    const onDelete = (key: string) => {
      const newConfig = { ...config };
      delete newConfig[key];
      onUpdateConfig(newConfig);
    };
    const onEditKeyValue = (key: string, value: string, isNew = false) => {
      const newKeyValue: { [key: string]: string } = {};
      newKeyValue[key] = value;
      onUpdateConfig({ ...config, ...newKeyValue });
      if (isNew) {
        setHasNew(false);
        if (newButtonRef.current) {
          newButtonRef.current.removeAttribute('disabled');
          newButtonRef.current.focus();
        }
      }
    };
    const newButtonRef = useRef<HTMLButtonElement>(null);
    const handleAddNewLine = () => {
      setHasNew(true);
      props.onAddNewLine?.();
    };

    return (
      <div
        {...elementProps}
        ref={elementRef}
        className={classNames.root}
        data-test={props['data-test'] || 'key-value-pair-editor'}
      >
        <div className={classNames.header}>
          <div className={classNames.keyColumn}>{keyLabel || 'Key'}</div>
          <div className={classNames.valueColumn}>{valueLabel || 'Value'}</div>
          <div className={classNames.buttonColumn}>
            {!readOnly && !editOnly && (
              <button
                data-test={'add-new-key-value-pair'}
                ref={newButtonRef}
                className={classNames.addButton}
                disabled={hasNew && !directlyEditable}
                onClick={handleAddNewLine}
              >
                <span>+</span>
              </button>
            )}
          </div>
        </div>
        <div className={classNames.body}>
          {(!items || items.length === 0) && !hasNew ? (
            <p className={classNames.emptyState}>{emptyStateMessage}</p>
          ) : (
            items.map((item): JSX.Element => {
              return (
                <KeyValuePair
                  ref={keyValueRef}
                  key={`${item[0]}--key-value`}
                  keyValue={item}
                  onDelete={onDelete}
                  onEdit={onEditKeyValue}
                  validation={validation}
                  allowEmptyValues={allowEmptyValues}
                  valueLabel={valueLabel}
                  isNew={false}
                  readOnly={readOnly}
                  editOnly={editOnly}
                  directlyEditable={directlyEditable}
                />
              );
            })
          )}
          {hasNew && (
            <KeyValuePair
              ref={keyValueRef}
              keyValue={['', '']}
              validation={validation}
              allowEmptyValues={allowEmptyValues}
              valueLabel={valueLabel}
              keyLabel={keyLabel}
              onEdit={onEditKeyValue}
              isNew={true}
              onClose={() => setHasNew(false)}
              readOnly={readOnly}
              editOnly={editOnly}
              directlyEditable={directlyEditable}
            />
          )}
        </div>
      </div>
    );
  }
);
